import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { useUpdateEntitlements } from '@/hooks/useEntitlements';
import { Settings, ShieldCheck, Zap, Crown, Lock, Unlock, FileText } from 'lucide-react';
import { toast } from 'sonner';

const getSourceBadge = (source: string, enabled: boolean) => {
  if (source === 'plan') return { label: '📋 Plan', variant: 'secondary' as const, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' };
  if (source === 'workspace_override' || source === 'override') return { label: '🔧 Admin Override', variant: 'outline' as const, className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' };
  if (source === 'owner') return { label: '👑 Owner', variant: 'default' as const, className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' };
  if (!enabled) return { label: '🔒 Upgrade Required', variant: 'destructive' as const, className: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' };
  return { label: source, variant: 'outline' as const, className: '' };
};

export default function AccessControl() {
  const { currentWorkspace } = useWorkspace();
  const moduleAccess = useModuleAccess('workspace', currentWorkspace?.id || '');
  const updateEntitlements = useUpdateEntitlements();

  const handlePlanChange = async (planCode: string) => {
    if (!currentWorkspace?.id) return;
    try {
      await updateEntitlements.mutateAsync({ action: 'update_plan', scope: 'workspace', scope_id: currentWorkspace.id, plan_code: planCode });
      toast.success(`Plan updated to ${planCode}`);
    } catch (error) {
      console.error('Plan update error:', error);
      toast.error('Failed to update plan');
    }
  };

  const handleModuleToggle = async (moduleId: string, enabled: boolean) => {
    if (!currentWorkspace?.id) return;
    try {
      await updateEntitlements.mutateAsync({ action: 'set_overrides', scope: 'workspace', scope_id: currentWorkspace.id, overrides: [{ module_id: moduleId, enabled }] });
      toast.success(`Module ${moduleId} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Module toggle error:', error);
      toast.error('Failed to update module');
    }
  };

  if (!moduleAccess.isOwner && !moduleAccess.hasPermission('workspace_admin', 'admin')) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardHeader>
            <ShieldCheck className="h-8 w-8 text-muted-foreground mb-2" />
            <CardTitle>Access Denied</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Only workspace owners and administrators can access this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (moduleAccess.isLoading) {
    return <div className="container mx-auto p-6"><div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Loading access control...</div></div></div>;
  }

  const disabledModules = moduleAccess.modules.filter(m => !m.enabled);

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Access Control</h1>
          <p className="text-muted-foreground">Manage plans, modules, and user permissions for {currentWorkspace?.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1"><ShieldCheck className="h-3 w-3" />{moduleAccess.isOwner ? 'Owner' : moduleAccess.userRole}</Badge>
          <Badge variant="secondary">{moduleAccess.enabledModulesCount} modules enabled</Badge>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5" />Current Plan</CardTitle>
          <CardDescription>Manage your workspace subscription and included modules</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg">{moduleAccess.planName || 'No Plan'}</h3>
              <p className="text-sm text-muted-foreground">Plan Code: {moduleAccess.planCode || 'N/A'}</p>
            </div>
            <Select value={moduleAccess.planCode || ''} onValueChange={handlePlanChange}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Select plan" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="pro">Professional</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <h4 className="font-medium mb-2">Included Modules:</h4>
            <div className="flex flex-wrap gap-2">
              {moduleAccess.modules.filter(m => m.enabled && m.source === 'plan').map(module => (
                <Badge key={module.module_id} variant="secondary" className="gap-1"><FileText className="h-3 w-3" />{module.module_id}</Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" />Module Access</CardTitle>
          <CardDescription>Control which modules are enabled. Use the toggle or quick enable button.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {moduleAccess.modules.map(module => {
              const sourceBadge = getSourceBadge(module.source, module.enabled);
              return (
                <div key={module.module_id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium">{module.module_id}</h3>
                      <Badge variant={sourceBadge.variant} className={`text-xs ${sourceBadge.className}`}>{sourceBadge.label}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{module.enabled ? 'Module is enabled' : 'Module is disabled'}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    {!module.enabled && module.source !== 'owner' && (
                      <Button size="sm" variant="outline" onClick={() => handleModuleToggle(module.module_id, true)} disabled={updateEntitlements.isPending} className="gap-1">
                        <Unlock className="h-3 w-3" />Enable for Trial
                      </Button>
                    )}
                    <Switch checked={module.enabled} onCheckedChange={(enabled) => handleModuleToggle(module.module_id, enabled)} disabled={module.source === 'owner' || updateEntitlements.isPending} />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {disabledModules.length > 0 && (
        <Card className="border-amber-200 dark:border-amber-800">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400"><Lock className="h-5 w-5" />Modules Available for Upgrade</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {disabledModules.map(module => (
                <Button key={module.module_id} variant="outline" size="sm" onClick={() => handleModuleToggle(module.module_id, true)} disabled={updateEntitlements.isPending} className="gap-1">
                  <Unlock className="h-3 w-3" />Enable {module.module_id}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {moduleAccess.isOwner && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Crown className="h-5 w-5" />Owner Tools</CardTitle>
            <CardDescription>Advanced management capabilities for workspace owners</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Button variant="outline" disabled>Bulk Operations</Button>
              <Button variant="outline" disabled>User Management</Button>
              <Button variant="outline" disabled>Audit Logs</Button>
            </div>
            <p className="text-sm text-muted-foreground mt-2">Advanced owner tools will be available in a future update.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}